import { type Cell, type Header, type Table, flexRender } from "@tanstack/react-table";
import { type FC, useLayoutEffect, useRef } from "react";

import { type ProcessedDataGridColumnDef } from "../useColumnState";
import { MaybeWrapInColumnReadonlyPopover } from "./MaybeWrapInColumnReadonlyPopover";

export const RowDetailCell: FC<{
	table: Table<unknown>;
	header: Header<unknown, unknown>;
	cell: Cell<unknown, unknown> | undefined;
	scrollIntoView: boolean;
}> = ({ table, header, cell, scrollIntoView }) => {
	const headerLabel = flexRender(header.column.columnDef.header, header.getContext());

	const columnDef = cell?.column.columnDef as
		| ProcessedDataGridColumnDef<unknown, unknown>
		| undefined;
	const cdCell = columnDef?.cell;
	let renderedCell: JSX.Element | React.ReactNode = null;

	if (cell) {
		// Create a copy to avoid modifying the original context
		const context = { ...cell.getContext(), renderAsRowDetail: true };
		renderedCell = (
			<MaybeWrapInColumnReadonlyPopover
				columnType={columnDef?.fieldType}
				columnReadonly={table.options.meta?.tableEditable === false || columnDef?.readonly}
				renderAsRowDetail
				value={flexRender(cdCell, context)}
			/>
		);
	}

	const ref = useRef<HTMLButtonElement>(null);
	const previouslyFocusedRef = useRef(false);
	useLayoutEffect(() => {
		if (!previouslyFocusedRef.current && scrollIntoView) {
			setTimeout(() => {
				ref.current?.scrollIntoView({
					behavior: "smooth",
					inline: "nearest",
				});
				ref.current?.focus();
				previouslyFocusedRef.current = true;
			}, 500);
		}
	}, [ref.current]);

	return (
		<div key={header.id} className="flex flex-col space-y-2">
			<div className="flex min-w-0">
				<button
					ref={ref}
					className="scroll-m-8 rounded focus:outline focus:outline-brand"
					onClick={(e) => {
						e.preventDefault();
						ref.current?.scrollIntoView({
							behavior: "smooth",
							inline: "nearest",
						});
					}}
				>
					<h4>{headerLabel}</h4>
				</button>
			</div>
			<div className="flex min-w-0">{renderedCell}</div>
		</div>
	);
};
